import React, { useState, useEffect, useCallback } from "react";
import { supabase } from "../../supabaseClient";
import Navigation from "../Navigation/Navigation";
import HomeFooter from "../HomePage/HomeFooter";
import stageIcon from "../../Assets/Images/mnt-podsused-logo.png";
import TennisIcon from "../../Assets/Icons/nogotenis.png";
import LoaderBox from "../HomePage/LoaderBox";

export default function AdminNogotenisTurnir({
  turnirId,
  selectedTurnirId,
  onBack,
}) {
  const currentTurnirId = turnirId || selectedTurnirId;

  const [matches, setMatches] = useState([]);
  const [turnirInfo, setTurnirInfo] = useState(null);
  const [loading, setLoading] = useState(true);
  const [teamCount, setTeamCount] = useState(32); // Default 32 tima

  const [zoom, setZoom] = useState(1);
  const [touchStartDist, setTouchStartDist] = useState(0);
  const [touchStartCenter, setTouchStartCenter] = useState({ x: 0, y: 0 });

  const getDistance = (touches) => {
    if (touches.length < 2) return 0;
    const dx = touches[0].clientX - touches[1].clientX;
    const dy = touches[0].clientY - touches[1].clientY;
    return Math.sqrt(dx * dx + dy * dy);
  };

  const handleTouchStart = (e) => {
    if (e.touches.length === 2) {
      const screenX = (e.touches[0].clientX + e.touches[1].clientX) / 2;
      const screenY = (e.touches[0].clientY + e.touches[1].clientY) / 2;
      setTouchStartCenter({ x: screenX, y: screenY });
      setTouchStartDist(getDistance(e.touches));
    }
  };

  const handleTouchMove = (e) => {
    if (e.touches.length === 2 && touchStartDist > 0) {
      e.preventDefault();

      const currentDist = getDistance(e.touches);
      const factor = currentDist / touchStartDist;
      const nextZoom = Math.max(0.4, Math.min(zoom * factor, 1.5));

      if (nextZoom !== zoom) {
        const container = e.currentTarget;
        const currentScrollLeft = container.scrollLeft;
        const currentScrollTop = container.scrollTop;
        const zoomRatio = nextZoom / zoom;

        setZoom(nextZoom);

        container.scrollLeft =
          (currentScrollLeft + touchStartCenter.x) * zoomRatio -
          touchStartCenter.x;
        container.scrollTop =
          (currentScrollTop + touchStartCenter.y) * zoomRatio -
          touchStartCenter.y;
      }
      setTouchStartDist(currentDist);
    }
  };

  const handleTouchEnd = () => {
    setTouchStartDist(0);
  };

  // Modal State
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [selectedSlotId, setSelectedSlotId] = useState(null);
  const [formData, setFormData] = useState({
    team_a_name: "",
    team_b_name: "",
    scorea: 0,
    scoreb: 0,
    match_time: "",
    is_completed: false,
  });

  const finalSlotId = teamCount - 1;
  const totalStages = Math.floor(Math.log2(teamCount));

  const fetchTurnirData = useCallback(async () => {
    const validTurnirId = currentTurnirId;
    if (!validTurnirId) {
      console.error("Greška: turnirId nije definiran!", currentTurnirId);
      setLoading(false);
      return;
    }

    const { data: turnirData, error: turnirError } = await supabase
      .from("admin_turniri")
      .select("*")
      .eq("id", validTurnirId)
      .single();

    if (turnirError) {
      console.error("Greška pri dohvatu info o turniru:", turnirError);
    } else {
      setTurnirInfo(turnirData);
    }

    const { data: matchesData, error: matchesError } = await supabase
      .from("turniri_nogotenis")
      .select("*")
      .eq("turnir_id", validTurnirId)
      .order("slot_id", { ascending: true });

    if (matchesError) {
      console.error("Greška pri dohvatu mečeva turnira:", matchesError);
      setLoading(false);
      return;
    }

    if (!matchesData || matchesData.length === 0) {
      const totalMatches = teamCount - 1;
      const initialMatches = [];

      for (let i = 1; i <= totalMatches; i++) {
        let stageName = "Faza";
        if (i === totalMatches) stageName = "Finale";
        else if (i >= totalMatches - 2) stageName = "Polufinale";
        else if (i >= totalMatches - 6) stageName = "Četvrtfinale";
        else if (i >= totalMatches - 14) stageName = "Osmina finala";
        else stageName = "Šesnaestina finala";

        initialMatches.push({
          turnir_id: validTurnirId,
          slot_id: i,
          stage_name: stageName,
          team_a_name: "?",
          team_b_name: "?",
          scorea: 0,
          scoreb: 0,
          is_completed: false,
          match_time: null,
        });
      }

      const { data: insertedData, error: insertError } = await supabase
        .from("turniri_nogotenis")
        .insert(initialMatches)
        .select();

      if (insertError) {
        console.error(
          "Greška pri inicijalizaciji praznih mečeva:",
          insertError,
        );
        setMatches([]);
      } else {
        setMatches(insertedData || []);
      }
    } else {
      setMatches(matchesData);
      const maxSlot = Math.max(...matchesData.map((m) => m.slot_id ?? 0));
      if (maxSlot === 63) setTeamCount(64);
      else if (maxSlot === 31) setTeamCount(32);
      else if (maxSlot === 15) setTeamCount(16);
      else if (maxSlot === 7) setTeamCount(8);
      else if (maxSlot === 3) setTeamCount(4);
    }
    setLoading(false);
  }, [currentTurnirId, teamCount]);

  useEffect(() => {
    const validTurnirId = currentTurnirId;
    if (!validTurnirId) {
      setLoading(false);
      return;
    }

    fetchTurnirData();

    const subscription = supabase
      .channel(`admin_nogotenis_turnir_${validTurnirId}`)
      .on(
        "postgres_changes",
        {
          event: "*",
          schema: "public",
          table: "turniri_nogotenis",
          filter: `turnir_id=eq.${validTurnirId}`,
        },
        () => fetchTurnirData(),
      )
      .subscribe();

    return () => {
      supabase.removeChannel(subscription);
    };
  }, [currentTurnirId, fetchTurnirData]);

  const handleResetTournament = async (count) => {
    const validTurnirId = currentTurnirId;
    const validCount = Number(count);

    if (!validTurnirId) {
      alert("Greška: ID turnira nedostaje.");
      return;
    }

    if (
      !window.confirm(
        `Jeste li sigurni da želite promijeniti veličinu turnira na ${validCount} timova? Postojeći timovi bit će sačuvani.`,
      )
    ) {
      return;
    }
    setLoading(true);
    setTeamCount(validCount);

    const totalMatches = validCount - 1;

    const { error: deleteError } = await supabase
      .from("turniri_nogotenis")
      .delete()
      .eq("turnir_id", validTurnirId)
      .gt("slot_id", totalMatches);

    if (deleteError) {
      console.error("Greška pri brisanju viška:", deleteError);
    }

    const updatedMatches = [];

    for (let i = 1; i <= totalMatches; i++) {
      let stageName = "Faza";
      if (i === totalMatches) stageName = "Finale";
      else if (i >= totalMatches - 2) stageName = "Polufinale";
      else if (i >= totalMatches - 6) stageName = "Četvrtfinale";
      else if (i >= totalMatches - 14) stageName = "Osmina finala";
      else stageName = "Šesnaestina finala";

      const postojećiMeč = matches.find((m) => m.slot_id === i);

      updatedMatches.push({
        turnir_id: validTurnirId,
        slot_id: i,
        stage_name: stageName,
        team_a_name: postojećiMeč ? postojećiMeč.team_a_name : "?",
        team_b_name: postojećiMeč ? postojećiMeč.team_b_name : "?",
        scorea: postojećiMeč ? postojećiMeč.scorea : 0,
        scoreb: postojećiMeč ? postojećiMeč.scoreb : 0,
        is_completed: postojećiMeč ? postojećiMeč.is_completed : false,
        match_time: postojećiMeč ? postojećiMeč.match_time : null,
      });
    }

    const { error: upsertError } = await supabase
      .from("turniri_nogotenis")
      .upsert(updatedMatches, { onConflict: "turnir_id,slot_id" });

    if (upsertError) {
      console.error("Greška pri spremanju stabla:", upsertError);
    }

    await fetchTurnirData();
  };

  const getMatchBySlot = (slotId) => {
    const match = matches.find((m) => m.slot_id === slotId);
    return (
      match || {
        slot_id: slotId,
        team_a_name: "?",
        team_b_name: "?",
        scorea: 0,
        scoreb: 0,
        match_time: null,
        is_completed: false,
      }
    );
  };

  const getWinner = () => {
    const finale = getMatchBySlot(finalSlotId);
    if (!finale || !finale.is_completed) return "?";
    return Number(finale.scorea) > Number(finale.scoreb)
      ? finale.team_a_name
      : finale.team_b_name;
  };

  const openEditModal = (slotId) => {
    const match = getMatchBySlot(slotId);
    setSelectedSlotId(slotId);
    setFormData({
      team_a_name: match.team_a_name === "?" ? "" : match.team_a_name || "",
      team_b_name: match.team_b_name === "?" ? "" : match.team_b_name || "",
      scorea: match.scorea || 0,
      scoreb: match.scoreb || 0,
      match_time: match.match_time || "",
      is_completed: match.is_completed || false,
    });
    setIsModalOpen(true);
  };

  const advanceWinner = async (
    currentSlot,
    teamAName,
    teamBName,
    scoreA,
    scoreB,
  ) => {
    if (currentSlot === finalSlotId) return;

    const winnerName = scoreA > scoreB ? teamAName : teamBName;
    if (!winnerName || winnerName === "?") return;

    let nextSlot = 0;
    let isTeamAInNextMatch = true;

    if (teamCount === 32) {
      if (currentSlot <= 16) {
        nextSlot = Math.floor((currentSlot - 1) / 2) + 17;
        isTeamAInNextMatch = currentSlot % 2 !== 0;
      } else if (currentSlot <= 24) {
        nextSlot = Math.floor((currentSlot - 17) / 2) + 25;
        isTeamAInNextMatch = currentSlot % 2 !== 0;
      } else if (currentSlot <= 28) {
        nextSlot = Math.floor((currentSlot - 25) / 2) + 29;
        isTeamAInNextMatch = currentSlot % 2 !== 0;
      } else if (currentSlot <= 30) {
        nextSlot = 31;
        isTeamAInNextMatch = currentSlot === 29;
      }
    } else if (teamCount === 16) {
      if (currentSlot <= 8) {
        nextSlot = Math.floor((currentSlot - 1) / 2) + 9;
        isTeamAInNextMatch = currentSlot % 2 !== 0;
      } else if (currentSlot <= 12) {
        nextSlot = Math.floor((currentSlot - 9) / 2) + 13;
        isTeamAInNextMatch = currentSlot % 2 !== 0;
      } else if (currentSlot <= 14) {
        nextSlot = 15;
        isTeamAInNextMatch = currentSlot === 13;
      }
    } else if (teamCount === 8) {
      if (currentSlot <= 4) {
        nextSlot = Math.floor((currentSlot - 1) / 2) + 5;
        isTeamAInNextMatch = currentSlot % 2 !== 0;
      } else if (currentSlot <= 6) {
        nextSlot = 7;
        isTeamAInNextMatch = currentSlot === 5;
      }
    } else if (teamCount === 4) {
      if (currentSlot <= 2) {
        nextSlot = 3;
        isTeamAInNextMatch = currentSlot === 1;
      }
    }

    if (nextSlot > 0) {
      const updatePayload = isTeamAInNextMatch
        ? { team_a_name: winnerName }
        : { team_b_name: winnerName };

      const { error } = await supabase
        .from("turniri_nogotenis")
        .update(updatePayload)
        .eq("turnir_id", currentTurnirId)
        .eq("slot_id", nextSlot);

      if (error) {
        console.error("Greška pri automatskom prebacivanju tima:", error);
      }
    }
  };

  const handleSaveChanges = async (e) => {
    e.preventDefault();

    const validTurnirId = currentTurnirId;
    if (!validTurnirId) {
      alert("Greška: ID turnira nije pronađen.");
      return;
    }

    const sA = Number(formData.scorea);
    const sB = Number(formData.scoreb);
    const isCompleted = sA > 0 || sB > 0 || formData.is_completed;

    const matchPayload = {
      turnir_id: validTurnirId,
      slot_id: Number(selectedSlotId),
      stage_name:
        selectedSlotId === finalSlotId
          ? "Finale"
          : `Faza - Slot ${selectedSlotId}`,
      team_a_name: formData.team_a_name || "?",
      team_b_name: formData.team_b_name || "?",
      scorea: sA,
      scoreb: sB,
      match_time: formData.match_time || null,
      is_completed: isCompleted,
    };

    const { error } = await supabase
      .from("turniri_nogotenis")
      .upsert(matchPayload, { onConflict: "turnir_id,slot_id" });

    if (error) {
      console.error("Greška pri spremanju:", error);
      alert("Došlo je do greške pri spremanju u bazu: " + error.message);
    } else {
      if (isCompleted && (formData.team_a_name || formData.team_b_name)) {
        await advanceWinner(
          selectedSlotId,
          formData.team_a_name,
          formData.team_b_name,
          sA,
          sB,
        );
      }
      setIsModalOpen(false);
      fetchTurnirData();
    }
  };

  const resetMatch = async () => {
    const validTurnirId = currentTurnirId;
    if (!window.confirm("Želiš li poništiti rezultat ove utakmice?")) return;

    const { error } = await supabase
      .from("turniri_nogotenis")
      .update({
        scorea: 0,
        scoreb: 0,
        is_completed: false,
      })
      .eq("turnir_id", validTurnirId)
      .eq("slot_id", selectedSlotId);

    if (error) {
      alert("Greška: " + error.message);
    } else {
      setIsModalOpen(false);
      fetchTurnirData();
    }
  };

  const renderMatchCard = (slotId, sideClass) => {
    const m = getMatchBySlot(slotId);

    return (
      <div
        key={slotId}
        className={`bela-match ${sideClass} admin-clickable`}
        onClick={() => openEditModal(slotId)}
      >
        <div className="team">
          <h4>{m.team_a_name || "?"}</h4>
          <span>{m.scorea}</span>
        </div>
        <div className="team">
          <h4>{m.team_b_name || "?"}</h4>
          <span>{m.scoreb}</span>
        </div>
        {m.match_time && <h5 className="match-info">{m.match_time}</h5>}
        <div className="vertical-line"></div>
      </div>
    );
  };

  const isFirstRound = (slotId) => {
    if (teamCount === 64) return slotId <= 32;
    if (teamCount === 32) return slotId <= 16;
    if (teamCount === 16) return slotId <= 8;
    if (teamCount === 8) return slotId <= 4;
    return slotId <= 2;
  };

  if (loading) return <LoaderBox text="Učitavanje admin panela..." />;

  const leftSidesColumns = [];
  const rightSidesColumns = [];
  let currentOffset = 0;

  for (let stage = totalStages; stage > 1; stage--) {
    const matchesInStageCount = Math.pow(2, stage - 1);
    const halfCount = matchesInStageCount / 2;

    const leftSlots = [];
    const rightSlots = [];

    for (let i = 1; i <= halfCount; i++) leftSlots.push(currentOffset + i);
    for (let i = 1; i <= halfCount; i++)
      rightSlots.push(currentOffset + halfCount + i);

    leftSidesColumns.push({ stageClass: `stage-${stage}`, slots: leftSlots });
    rightSidesColumns.unshift({
      stageClass: `stage-${stage}`,
      slots: rightSlots,
    });

    currentOffset += matchesInStageCount;
  }

  return (
    <>
      <Navigation
        naziv={turnirInfo?.naziv || "Nogotenis Turnir"}
        lokacija={turnirInfo?.lokacija || ""}
      />
      <div className="bela-turnir-wrapper admin-panel">
        <button className="back-btn" onClick={onBack}>
          <i className="fi fi-rs-arrow-left"></i> Natrag
        </button>
        <div className="admin-controls-header">
          <label htmlFor="team-select">BROJ TIMOVA:</label>
          <select
            id="team-select"
            value={teamCount}
            onChange={(e) => handleResetTournament(Number(e.target.value))}
          >
            <option value={4}>4 Tima</option>
            <option value={8}>8 Timova</option>
            <option value={16}>16 Timova</option>
            <option value={32}>32 Timova</option>
            <option value={64}>64 Timova</option>
          </select>
        </div>
        <div className="championship-stage-header">
          <img src={TennisIcon} alt="Stage" className="stage-img" />
          <h3>Uredi nogotenis turnir</h3>
        </div>
        <div
          className="bela-turnir-scroll"
          onTouchStart={handleTouchStart}
          onTouchMove={handleTouchMove}
          onTouchEnd={handleTouchEnd}
        >
          <div className="zoom-slider-container">
            <button
              type="button"
              className="zoom-reset-btn"
              onClick={() => setZoom(1)}
              title="Resetiraj na 100%"
            >
              {Math.round(zoom * 100)}%
            </button>
            <span className="zoom-label">
              <i className="fi fi-rs-zoom-in"></i>
            </span>
            <div className="slider-wrapper">
              <input
                type="range"
                min="0.4"
                max="1.5"
                step="0.05"
                value={zoom}
                onChange={(e) => setZoom(Number(e.target.value))}
                className="zoom-vertical-slider"
              />
            </div>
            <span className="zoom-label">
              <i className="fi fi-rs-zoom-out"></i>
            </span>
          </div>

          <div
            className="bela-turnir"
            style={{
              transform: `scale(${zoom})`,
              transformOrigin: "left center",
              transition:
                touchStartDist > 0 ? "none" : "transform 0.1s ease-out",
              width: "max-content",
              height: "max-content",
              maxWidth: `calc(100% * ${zoom})`,
              display: "flex",
              alignItems: "center",
              gap: "0px",
            }}
          >
            <div className="left-side">
              {leftSidesColumns.map((col, idx) => (
                <div
                  key={`left-grid-${idx}`}
                  className={`stage-column ${col.stageClass}`}
                >
                  {col.slots.map((id) => renderMatchCard(id, "match-left"))}
                </div>
              ))}
            </div>

            <div className="middle-side">
              <div className="final-content-wrapper">
                <div
                  className="bela-match finale-box admin-clickable"
                  onClick={() => openEditModal(finalSlotId)}
                >
                  {(() => {
                    const m = getMatchBySlot(finalSlotId);
                    return (
                      <>
                        <div className="team">
                          <h4>{m.team_a_name || "?"}</h4>
                          <span>{m.scorea}</span>
                        </div>
                        <div className="team">
                          <h4>{m.team_b_name || "?"}</h4>
                          <span>{m.scoreb}</span>
                        </div>
                        {m.match_time && (
                          <h5 className="match-info-final">{m.match_time}</h5>
                        )}
                      </>
                    );
                  })()}
                </div>

                <div className="trophy-container">
                  <img src={stageIcon} alt="Trophy" className="stage-img" />
                  <h3>POBJEDNIK</h3>
                  <div className="winner-box">{getWinner()}</div>
                </div>
              </div>
            </div>

            <div className="right-side">
              {rightSidesColumns.map((col, idx) => (
                <div
                  key={`right-grid-${idx}`}
                  className={`stage-column ${col.stageClass}`}
                >
                  {col.slots.map((id) => renderMatchCard(id, "match-right"))}
                </div>
              ))}
              <div style={{ minWidth: "30px", height: "1px", flexShrink: 0 }} />
            </div>
          </div>
        </div>

        {isModalOpen && (
          <div className="modal-backdrop" onClick={() => setIsModalOpen(false)}>
            <div className="modal-content" onClick={(e) => e.stopPropagation()}>
              <h3>Uredi meč - Utakmica #{selectedSlotId}</h3>
              <form onSubmit={handleSaveChanges}>
                <div className="form-group">
                  <label htmlFor="team_a_name_input">Tim A Naziv</label>
                  <input
                    id="team_a_name_input"
                    type="text"
                    value={formData.team_a_name}
                    disabled={!isFirstRound(selectedSlotId)}
                    onChange={(e) =>
                      setFormData({ ...formData, team_a_name: e.target.value })
                    }
                    placeholder={
                      isFirstRound(selectedSlotId)
                        ? "Upiši ime tima"
                        : "Ime se povlači automatski"
                    }
                  />
                </div>

                <div className="form-group">
                  <label htmlFor="team_b_name_input">Tim B Naziv</label>
                  <input
                    id="team_b_name_input"
                    type="text"
                    value={formData.team_b_name}
                    disabled={!isFirstRound(selectedSlotId)}
                    onChange={(e) =>
                      setFormData({ ...formData, team_b_name: e.target.value })
                    }
                    placeholder={
                      isFirstRound(selectedSlotId)
                        ? "Upiši ime tima"
                        : "Ime se povlači automatski"
                    }
                  />
                </div>

                <div className="form-row">
                  <div className="form-group">
                    <label htmlFor="score_a_input">Rezultat Tim A</label>
                    <input
                      id="score_a_input"
                      type="number"
                      min="0"
                      value={formData.scorea}
                      onChange={(e) =>
                        setFormData({ ...formData, scorea: e.target.value })
                      }
                    />
                  </div>
                  <div className="form-group">
                    <label htmlFor="score_b_input">Rezultat Tim B</label>
                    <input
                      id="score_b_input"
                      type="number"
                      min="0"
                      value={formData.scoreb}
                      onChange={(e) =>
                        setFormData({ ...formData, scoreb: e.target.value })
                      }
                    />
                  </div>
                </div>

                <div className="form-row">
                  <div className="form-group" style={{ width: "100%" }}>
                    <label htmlFor="match_time_input">Vrijeme utakmice</label>
                    <input
                      id="match_time_input"
                      type="text"
                      value={formData.match_time}
                      onChange={(e) =>
                        setFormData({ ...formData, match_time: e.target.value })
                      }
                      placeholder="Npr. 16:00"
                    />
                  </div>
                </div>

                <div className="modal-actions">
                  <button
                    type="button"
                    className="btn-cancel"
                    onClick={() => setIsModalOpen(false)}
                  >
                    Odustani
                  </button>

                  <button type="submit" className="btn-save">
                    Spremi
                  </button>
                </div>
                <button
                  type="button"
                  className="btn-reset"
                  onClick={resetMatch}
                  style={{
                    backgroundColor: "#ff4d4d",
                    color: "white",
                    marginRight: "10px",
                  }}
                >
                  Poništi rezultat
                </button>
              </form>
            </div>
          </div>
        )}
      </div>
      <HomeFooter />
    </>
  );
}
